/**
 * atomberg-offers.js
 * ──────────────────
 * Handles the EMI / Cashback / UPI offers section inside the Coupons & Offers
 * sheet on the PDP. Responsibilities:
 *
 *   - Accordion expand/collapse for banks and EMI tenures
 *   - EMI monthly-amount calculation from current variant price
 *   - Updates all amounts when the PDP variant changes
 *   - "Buy now at ₹X/mo" AJAX add-to-cart with fly-to-cart animation
 *
 * Price inputs: Shopify variant prices are in the smallest currency unit (paise).
 * The container carries data-offers-price-paise which is updated on variant change.
 */
(function () {
  if (/** @type {any} */ (window)._atombergOffersInit) return;
  /** @type {any} */ (window)._atombergOffersInit = true;

  // ─── EMI calculation ─────────────────────────────────────────────────────────

  /**
   * Calculates the monthly EMI amount.
   * Matches the formula used in the React SSR project.
   * @param {number} priceRupees - Product price in rupees (NOT paise)
   * @param {number} months
   * @param {number} interestRate - Annual interest rate as % (0 for no-cost)
   * @returns {number}
   */
  function calcMonthly(priceRupees, months, interestRate) {
    if (interestRate === 0) {
      return Math.ceil(priceRupees / months);
    }
    var total = priceRupees * (1 + (interestRate / 100) * (months / 12));
    var rounded = Math.round(total);
    var remainder = rounded % months;
    if (remainder === 0) {
      return rounded / months;
    }
    // Ceiling on the decimal part when there's a remainder
    var exact = total / months;
    var intPart = Math.floor(exact);
    var decPart = exact - intPart;
    return intPart + Math.ceil(decPart * 100) / 100;
  }

  /**
   * Formats a rupee amount in Indian locale with up to 2 decimal places.
   * @param {number} amount
   * @returns {string}
   */
  function fmtRupees(amount) {
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('en-IN');
    }
    return parseFloat(amount.toFixed(2)).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  }

  // ─── DOM update ──────────────────────────────────────────────────────────────

  /**
   * Updates all EMI amounts inside one offers container based on price.
   * @param {HTMLElement} container - element with [data-atomberg-offers]
   * @param {number} priceRupees
   */
  function updateAmounts(container, priceRupees) {
    // Update each bank's "from ₹X/month" subtitle (min monthly across tenures)
    container.querySelectorAll('[data-offers-bank]').forEach(function (bank) {
      var tenures = bank.querySelectorAll('[data-offers-tenure]');
      if (!tenures.length) return;

      var minMonthly = Infinity;
      tenures.forEach(function (tenure) {
        var months = parseInt(tenure.getAttribute('data-months') || '0', 10);
        var rate = parseFloat(tenure.getAttribute('data-rate') || '0');
        if (months < 1) return;
        var monthly = calcMonthly(priceRupees, months, rate);
        if (monthly < minMonthly) minMonthly = monthly;

        // Update tenure monthly-amount spans
        tenure.querySelectorAll('[data-offers-monthly]').forEach(function (el) {
          el.textContent = fmtRupees(monthly);
        });

        // Update breakdown: price, interest, total
        var priceEl = tenure.querySelector('[data-offers-price-display]');
        var interestEl = tenure.querySelector('[data-offers-interest-display]');
        var totalEl = tenure.querySelector('[data-offers-total-display]');
        var totalRowEl = tenure.querySelector('[data-offers-total-row]');

        if (priceEl) priceEl.textContent = '₹' + fmtRupees(priceRupees);

        if (rate === 0) {
          if (interestEl) interestEl.textContent = '+ ₹0';
          if (totalEl) totalEl.textContent = '₹' + fmtRupees(priceRupees);
          if (totalRowEl) {
            var m = calcMonthly(priceRupees, months, 0);
            totalRowEl.textContent = '(' + months + ' × ₹' + fmtRupees(m) + ')';
          }
        } else {
          var interest = Math.round(priceRupees * (rate / 100) * (months / 12));
          var totalAmt = priceRupees + interest;
          var mAmt = calcMonthly(priceRupees, months, rate);
          if (interestEl) interestEl.textContent = '+ ₹' + fmtRupees(interest);
          if (totalEl) totalEl.textContent = '₹' + fmtRupees(totalAmt);
          if (totalRowEl) {
            totalRowEl.textContent = '(' + months + ' × ₹' + fmtRupees(mAmt) + ')';
          }
        }
      });

      // Update "from ₹X/month"
      var fromEl = bank.querySelector('[data-offers-from]');
      if (fromEl && minMonthly !== Infinity) {
        fromEl.textContent = '₹' + fmtRupees(minMonthly);
      }
    });
  }

  // ─── Accordion ───────────────────────────────────────────────────────────────

  /**
   * Toggles open/closed state on an accordion element.
   * @param {HTMLElement} el - element with [data-open] semantics
   */
  function toggleAccordion(el) {
    if (el.hasAttribute('data-open')) {
      el.removeAttribute('data-open');
    } else {
      el.setAttribute('data-open', '');
    }
  }

  // ─── Fly-to-cart (reuses Horizon's <fly-to-cart> custom element) ─────────────

  /**
   * Triggers Horizon's fly-to-cart animation.
   * @param {HTMLElement} trigger - element to animate FROM
   */
  function flyToCart(trigger) {
    var cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon) return;
    var el = /** @type {any} */ (document.createElement('fly-to-cart'));
    el.classList.add('fly-to-cart--main');
    el.style.setProperty('--start-opacity', '0');
    el.source = trigger;
    el.destination = cartIcon;
    document.body.appendChild(el);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────

  /**
   * @param {HTMLElement} container
   */
  function init(container) {
    var priceAttr = container.getAttribute('data-offers-price-paise');
    var priceRupees = priceAttr ? parseInt(priceAttr, 10) / 100 : 0;

    // Initial amount render
    updateAmounts(container, priceRupees);

    // Bank accordion triggers
    container.querySelectorAll('[data-offers-bank-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var bank = /** @type {HTMLElement|null} */ (btn.closest('[data-offers-bank]'));
        if (bank) toggleAccordion(bank);
      });
    });

    // Tenure accordion triggers
    container.querySelectorAll('[data-offers-tenure-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tenure = /** @type {HTMLElement|null} */ (btn.closest('[data-offers-tenure]'));
        if (tenure) toggleAccordion(tenure);
      });
    });

    // The PDP deliberately keeps sold-out variants SELECTABLE (no dead
    // clicks), so this sheet's buy buttons must gate on availability
    // themselves — the main ATC/accelerated-checkout gating never reaches
    // in here.
    function syncBuyAvailability() {
      var available = container.getAttribute('data-offers-available') !== 'false';
      container.querySelectorAll('[data-offers-buy-btn]').forEach(function (btn) {
        /** @type {HTMLButtonElement} */ (btn).disabled = !available;
      });
    }
    syncBuyAvailability();

    // "Buy now at ₹X/mo" buttons — AJAX ATC + fly-to-cart
    container.querySelectorAll('[data-offers-buy-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = container.getAttribute('data-offers-variant-id');
        if (!variantId || /** @type {HTMLButtonElement} */ (btn).disabled) return;
        // Backstop for the disabled-attribute gate above (devtools edits,
        // stale DOM) — never add a sold-out variant.
        if (container.getAttribute('data-offers-available') === 'false') return;

        /** @type {HTMLButtonElement} */ (btn).disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then(function (r) {
            // 422 = Shopify refused (sold out / not enough stock) — without
            // this check the fly-to-cart animation and cart:update fired on
            // FAILED adds too, telling the shopper it worked.
            if (!r.ok) throw new Error('cart_add_failed');
            return r.json();
          })
          .then(function () {
            flyToCart(/** @type {HTMLElement} */ (btn));
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: { data: { itemCount: 1, source: 'product-form-component' } },
              })
            );
            setTimeout(function () {
              /** @type {HTMLButtonElement} */ (btn).disabled = false;
            }, 1500);
          })
          .catch(function () {
            /** @type {HTMLButtonElement} */ (btn).disabled = false;
          });
      });
    });

    // Listen for variant changes from the PDP picker and refresh amounts
    document.addEventListener('atomberg:pdp-variant-change', function (e) {
      var variant = /** @type {any} */ (e).detail?.variant;
      if (!variant) return;
      priceRupees = (variant.price || 0) / 100;
      container.setAttribute('data-offers-price-paise', String(variant.price || 0));
      container.setAttribute('data-offers-variant-id', String(variant.id));
      container.setAttribute('data-offers-available', variant.available ? 'true' : 'false');
      updateAmounts(container, priceRupees);
      syncBuyAvailability();
    });
  }

  // ─── EMI card calculated subtext ─────────────────────────────────────────────
  // The scroller card's "Starting from ₹X/Month" line (rendered when the shop's
  // show_calculated_bank_emi flag is on) lives outside [data-atomberg-offers],
  // so it needs its own variant-change refresh.
  document.addEventListener('atomberg:pdp-variant-change', function (e) {
    var variant = /** @type {any} */ (e).detail?.variant;
    if (!variant) return;
    document.querySelectorAll('[data-emi-card-subtext]').forEach(function (el) {
      var months = parseInt(el.getAttribute('data-emi-months') || '0', 10);
      if (!months) return;
      var monthly = calcMonthly((variant.price || 0) / 100, months, 0);
      el.textContent = 'Starting from ₹' + fmtRupees(monthly) + '/Month';
    });
  });

  // ─── Offer card scroll-to-section ────────────────────────────────────────────
  // When a [data-offer-scroll-to] card opens the sheet, scroll the sheet body
  // to the matching section after the sheet has opened (next paint).
  document.addEventListener('click', function (e) {
    var trigger = /** @type {HTMLElement} */ (e.target).closest('[data-offer-scroll-to]');
    if (!trigger) return;
    var target = trigger.getAttribute('data-offer-scroll-to');
    if (!target) return;

    // Sheet opens synchronously on click; scroll in next frame after paint
    requestAnimationFrame(function () {
      var section = document.getElementById('offers-section-' + target);
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  function start() {
    document.querySelectorAll('[data-atomberg-offers]').forEach(function (el) {
      init(/** @type {HTMLElement} */ (el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
